import React, { useCallback } from "react";
import styled, { css } from "styled-components";
import { FontSize, FontWeight, form, message, transparentize } from "@glossy";
import {
  SIZES,
  Size,
  VARIANTS,
  Variant,
  height,
  padding,
  radius,
} from "@ui-kit/form/styles";
import Icon, { Glyph } from "@ui-kit/icon";

type IProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> & {
  value?: string;
  hasError?: boolean;
  onChange: (value: string) => void;
  onEnterPress?: (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => void;
  $size?: Size;
  variant?: Variant;
  icon?: Glyph;
  isClearable?: boolean;
  customButton?: React.ReactNode;
  className?: string;
};

const Root = styled.div`
  position: relative;
  color: ${form};
`;

export const ClearButton = styled.button<{
  $size: Size; // "$size" to avoid conflict with HTML input "size" property
}>`
  position: absolute;
  color: inherit;
  top: 50%;
  transform: translateY(-50%);
  right: ${(props) => (SIZES[props.$size].height - 24) / 2}px;
  cursor: pointer;
  background: transparent;
  border-radius: 50%;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  :hover {
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
  :focus {
    background-color: ${(props) => transparentize(0.8, form(props, "primary"))};
  }
`;

const Input = styled.input<{
  $size: Size; // "$size" to avoid conflict with HTML input "size" property
  variant: Variant;
  disabled: boolean;
  isClearable: boolean;
  hasError?: boolean;
}>`
  line-height: ${height}px;
  padding: 0
    ${(props) => SIZES[props.$size].padding * (props.isClearable ? 2 : 1)}px 0
    ${padding}px;
  border-radius: ${radius}px;
  color: inherit;
  font-weight: ${FontWeight.Normal};
  font-size: ${FontSize.Medium};
  text-overflow: ellipsis;
  opacity: ${(props) => props.disabled && 0.5};
  width: ${(props) =>
    props.width === undefined ? "100%" : props.width + "px"};
  box-sizing: border-box;
  ::placeholder {
    opacity: 0.7;
    color: ${form};
  }

  ${(props) => VARIANTS[props.variant]};
  ${({ hasError, ...props }) =>
    hasError &&
    css`
      box-shadow: ${`0 0 0 2px ${message(props, "error")} inset`};
      background-color: transparent;
      &:focus {
        padding-right: 36px;
      }
    `};
`;

const CustomButton = styled.div<{
  $size: Size;
}>`
  position: absolute;
  top: 50%;
  right: ${radius}px;
  transform: translateY(-50%);
`;

const StyledIcon = styled(Icon)<{
  $size: Size;
}>`
  position: absolute;
  top: 50%;
  right: ${padding}px;
  transform: translateY(-50%);
  opacity: 0.5;
`;

const useKeyPress = (onEnterPress: IProps["onEnterPress"]) =>
  useCallback(
    (e) => {
      e.key === "Enter" &&
        e.target.value !== "" &&
        onEnterPress &&
        onEnterPress(e);
    },
    [onEnterPress]
  );

const UiKitFormText = ({
  value = "",
  $size = "m",
  variant = "secondary",
  icon,
  disabled = false,
  isClearable = true,
  onChange,
  onEnterPress,
  className,
  customButton,
  type = "text",
  autoFocus = false,
  ...props
}: IProps) => {
  const handleKeyPress = useKeyPress(onEnterPress);
  const showClearButton = isClearable && value?.length > 0 && !disabled;

  return (
    <Root className={className}>
      <Input
        autoFocus={autoFocus}
        type={type}
        value={value}
        $size={$size}
        variant={variant}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={handleKeyPress}
        onSubmit={handleKeyPress}
        isClearable={isClearable}
        {...props}
      />
      {showClearButton && (
        <ClearButton
          onClick={(event) => {
            if (event.detail !== 0) {
              onChange("");
            }
          }}
          $size={$size}
        >
          <Icon glyph="cross-thick" size="xs" />
        </ClearButton>
      )}
      {customButton && (
        <CustomButton $size={$size}>{customButton}</CustomButton>
      )}
      {icon && !showClearButton && <StyledIcon $size={$size} glyph={icon} />}
    </Root>
  );
};

export default UiKitFormText;
